import {
  areAmountValuesEquivalent,
  createOverheadsSnapshot,
  readServerValues,
  toDraftValue,
  validateFieldDraft,
  validateRelatedParentChildrenTotal,
} from "./overheadsAutosave.model";
import {
  VALUE_HISTORY_SWITCH_KIND,
  VALUE_HISTORY_SWITCH_REQUESTED,
  isCurrentCommittedValueHistorySwitch,
  isCurrentPersistenceIntent,
  isValueHistorySwitchIntent,
} from "./overheadsAutosave.history";

export const createInitialOverheadsAutosaveState = ({ companyInputId, overheads }) => {
  const snapshot = createOverheadsSnapshot(overheads);
  return {
    sessionKey: companyInputId ?? null,
    ...snapshot,
    persistence: {
      phase: "idle",
      pendingByField: {},
      inFlight: null,
      error: null,
    },
    valueHistory: null,
    nextIntentSequence: 1,
    nextValueHistoryGeneration: 1,
    lastSuccessfulSaveAt: null,
  };
};

const confirmedPhase = (field) => (field.confirmedValue == null && field.lastSavedAt == null ? "pristine" : "saved");

const removePendingField = (pendingByField, fieldKey) => {
  if (!Object.prototype.hasOwnProperty.call(pendingByField, fieldKey)) return pendingByField;
  const nextPending = { ...pendingByField };
  delete nextPending[fieldKey];
  return nextPending;
};

const getRelatedParentAccountId = (field) => {
  if (!field) return null;
  return field.kind === "child" ? field.parentAccountId : field.accountId;
};

const clearSubcategoryTotalErrors = (fields, parentAccountId) => {
  if (parentAccountId == null) return fields;
  let nextFields = fields;
  Object.values(fields).forEach((candidate) => {
    if (!candidate || candidate.error?.kind !== "subcategory-total") return;
    const candidateParentAccountId = getRelatedParentAccountId(candidate);
    if (candidateParentAccountId !== parentAccountId) return;
    if (nextFields === fields) nextFields = { ...fields };
    nextFields[candidate.key] = {
      ...candidate,
      phase: areAmountValuesEquivalent(candidate.draftValue, candidate.confirmedValue) ? confirmedPhase(candidate) : "editing",
      error: null,
    };
  });
  return nextFields;
};

const queueField = (state, fieldKey, { kind = "save", historyGeneration = null, historyCurrentValue = null, historyAlternateValue = null } = {}) => {
  const field = state.fields[fieldKey];
  if (!field) return state;

  if (areAmountValuesEquivalent(field.draftValue, field.confirmedValue)) {
    return {
      ...state,
      fields: {
        ...state.fields,
        [fieldKey]: {
          ...field,
          phase: confirmedPhase(field),
          error: null,
        },
      },
      persistence: {
        ...state.persistence,
        pendingByField: removePendingField(state.persistence.pendingByField, fieldKey),
      },
    };
  }

  const validation = validateFieldDraft(field, field.draftValue);
  if (!validation.isValid) {
    return {
      ...state,
      fields: {
        ...state.fields,
        [fieldKey]: {
          ...field,
          phase: "error",
          error: { kind: "validation", message: validation.message },
        },
      },
      persistence: {
        ...state.persistence,
        pendingByField: removePendingField(state.persistence.pendingByField, fieldKey),
      },
    };
  }

  const groupValidation = validateRelatedParentChildrenTotal(state, field);
  if (!groupValidation.isValid) {
    return {
      ...state,
      fields: { ...state.fields, [fieldKey]: { ...field, phase: "error", error: { kind: "subcategory-total" } } },
      persistence: { ...state.persistence, pendingByField: removePendingField(state.persistence.pendingByField, fieldKey) },
    };
  }

  const sequence = state.nextIntentSequence;
  return {
    ...state,
    fields: { ...state.fields, [fieldKey]: { ...field, phase: "queued", error: null } },
    persistence: {
      ...state.persistence,
      pendingByField: {
        ...state.persistence.pendingByField,
        [fieldKey]: {
          fieldKey,
          revision: field.revision,
          draftValue: field.draftValue,
          kind,
          sequence,
          historyGeneration,
          historyCurrentValue,
          historyAlternateValue,
        },
      },
    },
    nextIntentSequence: sequence + 1,
  };
};

const queueAllChangedFields = (state) => {
  let nextState = state;
  state.fieldOrder.forEach((fieldKey) => {
    const field = nextState.fields[fieldKey];
    if (!field) return;
    if (field.phase === "editing" || field.phase === "error") nextState = queueField(nextState, fieldKey);
  });
  return nextState;
};

const getLatestCommittedHistoryIntent = ({ state, includedByField, committedFieldKeys, serverValues }) =>
  Object.values(includedByField)
    .filter((intent) => {
      if (!committedFieldKeys.has(intent.fieldKey)) return false;
      if (intent.kind === "save") return !areAmountValuesEquivalent(serverValues[intent.fieldKey], intent.previousConfirmedValue);
      if (isValueHistorySwitchIntent(intent))
        return isCurrentCommittedValueHistorySwitch({ intent, valueHistory: state.valueHistory, field: state.fields[intent.fieldKey] });
      return false;
    })
    .sort((left, right) => right.sequence - left.sequence)[0] || null;

const removeStalePendingValueHistorySwitchIntents = ({ fields, pendingByField, valueHistory }) => {
  let nextFields = fields;
  let nextPending = pendingByField;

  Object.values(pendingByField).forEach((intent) => {
    if (!isValueHistorySwitchIntent(intent)) return;
    const field = nextFields[intent.fieldKey];
    const remainsValid = isCurrentPersistenceIntent({ intent, valueHistory, field });
    if (remainsValid) return;

    if (nextPending === pendingByField) nextPending = { ...pendingByField };
    delete nextPending[intent.fieldKey];

    if (field && field.revision === intent.revision) {
      if (nextFields === fields) nextFields = { ...fields };
      nextFields[intent.fieldKey] = { ...field, draftValue: toDraftValue(field.confirmedValue), phase: confirmedPhase(field), error: null };
    }
  });

  return { fields: nextFields, pendingByField: nextPending };
};

const applyAuthoritativeServerState = ({ state, serverOverheads, committedFieldKeys, completedAt, failedKind, failedMessage, fieldErrors }) => {
  const inFlight = state.persistence.inFlight;
  if (!inFlight) return state;

  const serverSnapshot = createOverheadsSnapshot(serverOverheads);
  const serverValues = readServerValues(serverOverheads);
  const allFieldKeys = Array.from(new Set([...state.fieldOrder, ...serverSnapshot.fieldOrder]));
  const nextFields = {};

  allFieldKeys.forEach((fieldKey) => {
    const oldField = state.fields[fieldKey];
    const serverField = serverSnapshot.fields[fieldKey];

    if (!oldField) {
      nextFields[fieldKey] = serverField;
      return;
    }

    const serverValue = Object.prototype.hasOwnProperty.call(serverValues, fieldKey) ? serverValues[fieldKey] : oldField.confirmedValue;
    const included = inFlight.includedByField[fieldKey];
    const pending = state.persistence.pendingByField[fieldKey];

    if (included) {
      const committed = committedFieldKeys.has(fieldKey);
      const requestStillCurrent = oldField.revision === included.revision;

      if (committed) {
        nextFields[fieldKey] = {
          ...oldField,
          confirmedValue: serverValue,
          draftValue: requestStillCurrent && !pending ? toDraftValue(serverValue) : oldField.draftValue,
          phase:
            requestStillCurrent && !pending
              ? "saved"
              : pending
              ? "queued"
              : areAmountValuesEquivalent(oldField.draftValue, serverValue)
              ? "saved"
              : "editing",
          error: null,
          lastSavedAt: completedAt,
        };
        return;
      }

      nextFields[fieldKey] = {
        ...oldField,
        confirmedValue: serverValue,
        draftValue: oldField.draftValue,
        phase:
          requestStillCurrent && !pending
            ? "error"
            : pending
            ? "queued"
            : areAmountValuesEquivalent(oldField.draftValue, serverValue)
            ? "saved"
            : "editing",
        error:
          requestStillCurrent && !pending
            ? {
                kind: failedKind || "server",
                message: fieldErrors?.[fieldKey] || failedMessage || "The value could not be saved.",
              }
            : null,
      };
      return;
    }

    const hasLocalDraft =
      !areAmountValuesEquivalent(oldField.draftValue, oldField.confirmedValue) ||
      oldField.phase === "editing" ||
      oldField.phase === "queued" ||
      oldField.phase === "error";

    nextFields[fieldKey] = {
      ...oldField,
      confirmedValue: serverValue,
      draftValue: hasLocalDraft ? oldField.draftValue : toDraftValue(serverValue),
      phase: hasLocalDraft ? oldField.phase : serverValue == null && oldField.phase === "pristine" ? "pristine" : "saved",
    };
  });

  const latestHistoryIntent = getLatestCommittedHistoryIntent({ state, includedByField: inFlight.includedByField, committedFieldKeys, serverValues });
  let nextValueHistory = state.valueHistory;
  let nextValueHistoryGeneration = state.nextValueHistoryGeneration;
  if (latestHistoryIntent?.kind === "save") {
    nextValueHistory = {
      generation: nextValueHistoryGeneration,
      ownerFieldKey: latestHistoryIntent.fieldKey,
      currentValue: serverValues[latestHistoryIntent.fieldKey],
      alternateValue: latestHistoryIntent.previousConfirmedValue,
    };
    nextValueHistoryGeneration += 1;
  } else if (isValueHistorySwitchIntent(latestHistoryIntent)) {
    nextValueHistory = {
      generation: nextValueHistoryGeneration,
      ownerFieldKey: latestHistoryIntent.fieldKey,
      currentValue: serverValues[latestHistoryIntent.fieldKey],
      alternateValue: latestHistoryIntent.historyCurrentValue,
    };
    nextValueHistoryGeneration += 1;
  }

  const cleaned = removeStalePendingValueHistorySwitchIntents({
    fields: nextFields,
    pendingByField: state.persistence.pendingByField,
    valueHistory: nextValueHistory,
  });

  return {
    ...state,
    structure: serverSnapshot.structure,
    fieldOrder: allFieldKeys,
    fields: cleaned.fields,
    persistence: {
      phase: "idle",
      pendingByField: cleaned.pendingByField,
      inFlight: null,
      error: null,
    },
    valueHistory: nextValueHistory,
    nextValueHistoryGeneration,
    lastSuccessfulSaveAt: committedFieldKeys.size > 0 ? completedAt : state.lastSuccessfulSaveAt,
  };
};

export function overheadsAutosaveReducer(state, action) {
  switch (action.type) {
    case "HYDRATE":
      return createInitialOverheadsAutosaveState({
        companyInputId: action.companyInputId,
        overheads: action.overheads,
      });

    case "FIELD_CHANGED": {
      const field = state.fields[action.fieldKey];
      if (!field) return state;
      const relatedParentAccountId = getRelatedParentAccountId(field);
      const changedFields = {
        ...state.fields,
        [action.fieldKey]: {
          ...field,
          draftValue: action.value == null ? "" : String(action.value),
          revision: field.revision + 1,
          phase: "editing",
          error: null,
        },
      };
      return {
        ...state,
        fields: clearSubcategoryTotalErrors(changedFields, relatedParentAccountId),
        persistence: {
          ...state.persistence,
          pendingByField: removePendingField(state.persistence.pendingByField, action.fieldKey),
        },
      };
    }

    case "FIELD_BLURRED":
      return queueField(state, action.fieldKey);

    case "QUEUE_ALL_CHANGED":
      return queueAllChangedFields(state);

    case "FIELD_REVERTED": {
      const field = state.fields[action.fieldKey];
      if (!field || field.phase === "saving") return state;
      const revertedFields = {
        ...state.fields,
        [action.fieldKey]: {
          ...field,
          draftValue: toDraftValue(field.confirmedValue),
          revision: field.revision + 1,
          phase: confirmedPhase(field),
          error: null,
        },
      };
      return {
        ...state,
        fields: clearSubcategoryTotalErrors(revertedFields, getRelatedParentAccountId(field)),
        persistence: {
          ...state.persistence,
          pendingByField: removePendingField(state.persistence.pendingByField, action.fieldKey),
        },
      };
    }

    case VALUE_HISTORY_SWITCH_REQUESTED: {
      const history = state.valueHistory;
      const field = state.fields[action.fieldKey];
      if (
        !history ||
        history.ownerFieldKey !== action.fieldKey ||
        !field ||
        field.phase !== "saved" ||
        !areAmountValuesEquivalent(field.confirmedValue, history.currentValue)
      ) {
        return state;
      }

      const revisedState = {
        ...state,
        fields: {
          ...state.fields,
          [action.fieldKey]: {
            ...field,
            draftValue: toDraftValue(history.alternateValue),
            revision: field.revision + 1,
            phase: "editing",
            error: null,
          },
        },
      };
      return queueField(revisedState, action.fieldKey, {
        kind: VALUE_HISTORY_SWITCH_KIND,
        historyGeneration: history.generation,
        historyCurrentValue: history.currentValue,
        historyAlternateValue: history.alternateValue,
      });
    }

    case "DROP_STALE_PENDING": {
      let pendingByField = state.persistence.pendingByField;
      const fields = { ...state.fields };
      action.fieldKeys.forEach((fieldKey) => {
        pendingByField = removePendingField(pendingByField, fieldKey);
        const field = fields[fieldKey];
        if (field?.phase === "queued") {
          fields[fieldKey] = {
            ...field,
            draftValue: toDraftValue(field.confirmedValue),
            phase: confirmedPhase(field),
            error: null,
          };
        }
      });
      return {
        ...state,
        fields,
        persistence: { ...state.persistence, pendingByField },
      };
    }

    case "REQUEST_STARTED": {
      if (state.persistence.inFlight) return state;
      let pendingByField = state.persistence.pendingByField;
      const fields = { ...state.fields };
      const includedByField = {};

      Object.values(action.includedByField).forEach((intent) => {
        const pending = pendingByField[intent.fieldKey];
        const field = fields[intent.fieldKey];
        const pendingMatches = pending && pending.sequence === intent.sequence && pending.revision === intent.revision;
        const intentIsCurrent = isCurrentPersistenceIntent({ intent, valueHistory: state.valueHistory, field });
        if (!pendingMatches) return;
        if (!intentIsCurrent) {
          pendingByField = removePendingField(pendingByField, intent.fieldKey);
          if (field?.phase === "queued" && field.revision === intent.revision) {
            fields[intent.fieldKey] = {
              ...field,
              draftValue: toDraftValue(field.confirmedValue),
              phase: confirmedPhase(field),
              error: null,
            };
          }
          return;
        }
        includedByField[intent.fieldKey] = intent;
        pendingByField = removePendingField(pendingByField, intent.fieldKey);
        fields[intent.fieldKey] = { ...field, phase: "saving", error: null };
      });
      if (Object.keys(includedByField).length === 0) {
        return { ...state, fields, persistence: { ...state.persistence, pendingByField } };
      }

      return {
        ...state,
        fields,
        persistence: {
          phase: "saving",
          pendingByField,
          inFlight: {
            requestId: action.requestId,
            includedByField,
          },
          error: null,
        },
      };
    }

    case "REQUEST_SUCCEEDED": {
      if (state.persistence.inFlight?.requestId !== action.requestId) return state;
      return applyAuthoritativeServerState({
        state,
        serverOverheads: action.serverOverheads,
        committedFieldKeys: new Set(Object.keys(state.persistence.inFlight.includedByField)),
        completedAt: action.completedAt,
      });
    }

    case "REQUEST_RECONCILING": {
      if (state.persistence.inFlight?.requestId !== action.requestId) return state;
      return {
        ...state,
        persistence: {
          ...state.persistence,
          phase: "reconciling",
          error: action.error,
        },
      };
    }

    case "RECONCILIATION_RETRY_STARTED":
      if (!state.persistence.inFlight) return state;
      return {
        ...state,
        persistence: { ...state.persistence, phase: "reconciling" },
      };

    case "RECONCILIATION_SUCCEEDED": {
      if (state.persistence.inFlight?.requestId !== action.requestId) return state;
      const serverValues = readServerValues(action.serverOverheads);
      const committedFieldKeys = new Set(
        Object.values(state.persistence.inFlight.includedByField)
          .filter((intent) => areAmountValuesEquivalent(serverValues[intent.fieldKey], intent.submittedApiValue))
          .map((intent) => intent.fieldKey)
      );

      return applyAuthoritativeServerState({
        state,
        serverOverheads: action.serverOverheads,
        committedFieldKeys,
        completedAt: action.completedAt,
        failedKind: action.failure?.kind || "network",
        failedMessage: action.failure?.message,
        fieldErrors: action.failure?.fieldErrors,
      });
    }

    case "RECONCILIATION_FAILED": {
      if (state.persistence.inFlight?.requestId !== action.requestId) return state;
      const fields = { ...state.fields };
      Object.values(state.persistence.inFlight.includedByField).forEach((intent) => {
        const field = fields[intent.fieldKey];
        const pending = state.persistence.pendingByField[intent.fieldKey];
        if (field && field.revision === intent.revision && !pending) {
          fields[intent.fieldKey] = {
            ...field,
            phase: "error",
            error: {
              kind: "network",
              message: action.message || "The save result could not be verified.",
            },
          };
        }
      });
      return {
        ...state,
        fields,
        persistence: {
          ...state.persistence,
          phase: "unverified",
          error: { kind: "network", message: action.message || "The save result could not be verified." },
        },
      };
    }

    default:
      return state;
  }
}
